//
// Reusable inline connections panel — embeds the Cytoscape graph, legend,
// and node detail into any page (DocumentDetail, FolderView, ProjectView).
// Supports pop-out fullscreen mode.
import { useState, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { GitBranch, ChevronDown, ChevronUp, Maximize2, Maximize, Minimize2, X } from 'lucide-react'
import ConnectionsGraph, { clearPositions } from './ConnectionsGraph'
import ConnectionsLegend from './ConnectionsLegend'
import NodeDetailPanel from './NodeDetailPanel'
import { exploreConnections } from '../../services/api'
import { isPlaceholderTopic } from '../../utils/topics'
import type { ConnectionsNode } from '../../types'

interface InlineConnectionsPanelProps {
  /** The resource ID to explore connections for (document_id typically) */
  resourceId: string | null
  /** Label shown in the header, e.g. "Document Connections" */
  title?: string
  /** BUG-NEW-J: project_id for namespace isolation — prevents cross-project leakage */
  projectId?: string
}

export default function InlineConnectionsPanel({ resourceId, title = 'Connections', projectId }: InlineConnectionsPanelProps) {
  const [selectedNode, setSelectedNode] = useState<ConnectionsNode | null>(null)
  const [collapsed, setCollapsed] = useState(false)
  const [poppedOut, setPoppedOut] = useState(false)
  const [hiddenNodeTypes, setHiddenNodeTypes] = useState<Set<string>>(new Set())
  const [hiddenEdgeTypes, setHiddenEdgeTypes] = useState<Set<string>>(new Set())
  // CoWork Round 3 (Story #2777 #2785 follow-up): the production graph is
  // mounted via THIS panel (not the deprecated /connections page), so the
  // Spread button has to live here. Clicking Spread clears any stored
  // localStorage positions for the resource and bumps resetCounter so
  // ConnectionsGraph re-runs the (50000-repulsion) auto-layout fresh —
  // this is the single mechanism that resolves the centre-cluster overlap
  // CoWork reported in R1/R2/R3 retests.
  const [resetCounter, setResetCounter] = useState(0)
  const handleSpread = useCallback(() => {
    if (resourceId) clearPositions(resourceId)
    setResetCounter((c) => c + 1)
  }, [resourceId])

  const toggleNodeType = useCallback((type: string) => {
    setHiddenNodeTypes(prev => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type); else next.add(type)
      return next
    })
  }, [])

  const toggleEdgeType = useCallback((type: string) => {
    setHiddenEdgeTypes(prev => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type); else next.add(type)
      return next
    })
  }, [])

  // Summary card data — dedupes with ConnectionsGraph's own query (same key + args)
  // so there is no extra network call.
  const { data: graphData } = useQuery({
    queryKey: ['connections', resourceId, projectId],
    queryFn: () => exploreConnections(resourceId!, projectId),
    enabled: !!resourceId,
  })

  const summaryStats = useMemo(() => {
    if (!graphData || !graphData.nodes) return null
    const journalCount = graphData.nodes.filter((n) => n.type === 'journal').length
    const collaboratorCount = graphData.nodes.filter((n) => n.type === 'collaborator').length
    const topTopics = graphData.nodes
      .filter((n) => n.type === 'topic')
      .slice()
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .map((t) => t.label)
      .filter((l): l is string => !!l && !isPlaceholderTopic(l))
      .slice(0, 2)
    return { journalCount, collaboratorCount, topTopics }
  }, [graphData])

  const summaryCard = summaryStats &&
    (summaryStats.journalCount > 0 || summaryStats.collaboratorCount > 0 || summaryStats.topTopics.length > 0) ? (
    <div
      className="px-4 py-3 bg-iflow-50 border-b border-iflow-200 text-sm text-gray-700 flex-shrink-0"
      data-testid="connections-summary-inline"
    >
      Your invention connects to{' '}
      <strong>{summaryStats.journalCount}</strong> related journal{summaryStats.journalCount === 1 ? '' : 's'}
      {summaryStats.collaboratorCount > 0 && (
        <>
          {' '}and{' '}
          <strong>{summaryStats.collaboratorCount}</strong> potential collaborator{summaryStats.collaboratorCount === 1 ? '' : 's'}
        </>
      )}
      .
      {summaryStats.topTopics.length > 0 && (
        <>
          {' '}Strongest connections: <strong>{summaryStats.topTopics.join(', ')}</strong>.
        </>
      )}
    </div>
  ) : null

  if (!resourceId) return null

  const graphContent = (
    <div className="flex w-full max-w-full overflow-hidden" style={{ minHeight: poppedOut ? 'calc(100vh - 48px)' : 420, maxHeight: poppedOut ? undefined : 'calc(100vh - 300px)', height: poppedOut ? 'calc(100vh - 48px)' : 'auto' }}>
      {/* Graph area — flex-1 fills available width, left-justified via layout gravity */}
      <div className="flex-1 relative" style={{ minHeight: poppedOut ? undefined : 420 }}>
        <ConnectionsGraph
          documentId={resourceId}
          layout="cose"
          onNodeSelect={setSelectedNode}
          hiddenNodeTypes={hiddenNodeTypes}
          hiddenEdgeTypes={hiddenEdgeTypes}
          projectId={projectId}
          resetCounter={resetCounter}
        />
      </div>

      {/* Legend sidebar — right side, clickable toggles */}
      <div className="w-32 border-l border-sky-200/30 px-2.5 py-2 flex-shrink-0 overflow-y-auto bg-white/60">
        <ConnectionsLegend
          hiddenNodeTypes={hiddenNodeTypes}
          hiddenEdgeTypes={hiddenEdgeTypes}
          onToggleNodeType={toggleNodeType}
          onToggleEdgeType={toggleEdgeType}
        />
      </div>

      {/* Node detail sidebar (inside the panel) */}
      {selectedNode && (
        <div className="w-56 border-l border-sky-200/30 p-3 flex-shrink-0 overflow-y-auto">
          <NodeDetailPanel
            node={selectedNode}
            onClose={() => setSelectedNode(null)}
            onDiscoveryStart={() => {}}
          />
        </div>
      )}
    </div>
  )

  // Fullscreen pop-out overlay
  if (poppedOut) {
    return (
      <>
        {/* Placeholder in the inline position so layout doesn't jump */}
        <div className="light-card p-4 text-center text-sm text-gray-500">
          Connections panel is in fullscreen mode.
          <button
            onClick={() => setPoppedOut(false)}
            className="ml-2 text-iflow-600 hover:text-iflow-700 font-medium"
          >
            Restore
          </button>
        </div>

        {/* Fullscreen overlay */}
        <div className="fixed inset-0 z-50 bg-[#e8eff4] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-sky-200/50 flex-shrink-0">
            <div className="flex items-center gap-2">
              <GitBranch size={16} className="text-iflow-500" />
              <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
            </div>
            <div className="flex items-center gap-1">
              {/* CoWork R3: Spread button mirrors the inline header. */}
              <button
                onClick={handleSpread}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 border border-gray-200 rounded-md hover:bg-white/30"
                title="Re-run layout — spreads dense clusters out so labels don't overlap"
                data-testid="connections-spread-button"
              >
                <Maximize size={14} /> Spread
              </button>
              <button
                onClick={() => setPoppedOut(false)}
                className="p-1.5 text-gray-500 hover:text-gray-600 rounded-md hover:bg-white/30"
                title="Minimize"
              >
                <Minimize2 size={16} />
              </button>
              <button
                onClick={() => { setPoppedOut(false); setCollapsed(true) }}
                className="p-1.5 text-gray-500 hover:text-gray-600 rounded-md hover:bg-white/30"
                title="Close"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          {/* Summary card (above graph, below header) */}
          {summaryCard}
          {/* Graph fills remaining space */}
          {graphContent}
        </div>
      </>
    )
  }

  return (
    <div className="light-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 hover:bg-white/30 transition-colors">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-2 flex-1"
        >
          <GitBranch size={16} className="text-iflow-500" />
          <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
          {collapsed ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronUp size={16} className="text-gray-500" />}
        </button>
        {!collapsed && (
          <div className="flex items-center gap-1">
            {/* CoWork R3 (Story #2777 #2785 follow-up): Spread button.
                Sits next to Fullscreen on the inline-mode header. */}
            <button
              onClick={handleSpread}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 border border-gray-200 rounded-md hover:bg-white/30"
              title="Re-run layout — spreads dense clusters out so labels don't overlap"
              data-testid="connections-spread-button-inline"
            >
              <Maximize size={14} /> Spread
            </button>
            <button
              onClick={() => setPoppedOut(true)}
              className="p-1.5 text-gray-500 hover:text-gray-600 rounded-md hover:bg-white/30"
              title="Fullscreen"
            >
              <Maximize2 size={14} />
            </button>
          </div>
        )}
      </div>

      {!collapsed && (
        <div className="border-t border-sky-200/30">
          {summaryCard}
          {graphContent}
        </div>
      )}
    </div>
  )
}
